// src/components/auth/authConfig.js

const ua = window.navigator.userAgent
const msie = ua.indexOf('MSIE ')
const msie11 = ua.indexOf('Trident/')
const msedge = ua.indexOf('Edge/')
const firefox = ua.indexOf('Firefox')

const isIE = msie > 0 || msie11 > 0
const isEdge = msedge > 0
const isFirefox = firefox > 0

const appOrigin = window.location.origin

export const interactiveRedirectUri = `${appOrigin}/app/dashboard`
export const silentRedirectUri = `${appOrigin}/auth-silent.html`

const normalise = (value) => (value || '').toLowerCase().trim()

const normaliseHost = (value) =>
  normalise(value).replace('https://', '').replace('http://', '').split('/')[0]

export const b2cPolicies = {
  names: {
    signUp: process.env.REACT_APP_B2C_SIGNUP_POLICY,
    signUpSignIn: process.env.REACT_APP_B2C_LOGIN_POLICY,
    editProfile: process.env.REACT_APP_B2C_EDIT_PROFILE_POLICY,
  },
  authorities: {
    signUp: {
      authority: process.env.REACT_APP_B2C_SIGNUP_AUTHORITY,
    },
    signUpSignIn: {
      authority: process.env.REACT_APP_B2C_LOGIN_AUTHORITY,
    },
    editProfile: {
      authority: process.env.REACT_APP_B2C_EDIT_PROFILE_AUTHORITY,
    },
  },
  authorityDomain: process.env.REACT_APP_B2C_AUTH_DOMAIN,
}

export const primaryAuthority = b2cPolicies.authorities.signUpSignIn.authority
export const registerAuthority = b2cPolicies.authorities.signUp.authority
export const primaryPolicyName = b2cPolicies.names.signUpSignIn

export const getAccountPolicy = (account) => {
  return account?.idTokenClaims?.tfp || account?.idTokenClaims?.acr || ''
}

export const accountMatchesPolicy = (account, policyName = primaryPolicyName) => {
  return normalise(getAccountPolicy(account)) === normalise(policyName)
}

export const accountMatchesAuthorityDomain = (account) => {
  const expectedDomain = normaliseHost(b2cPolicies.authorityDomain)

  if (!expectedDomain) {
    return true
  }

  const accountEnvironment = normaliseHost(account?.environment)

  if (!accountEnvironment) {
    return true
  }

  return accountEnvironment === expectedDomain
}

export const isPrimaryAccount = (account) => {
  return Boolean(
    account &&
      accountMatchesPolicy(account, primaryPolicyName) &&
      accountMatchesAuthorityDomain(account),
  )
}

export const getPrimaryAccount = (instance) => {
  const activeAccount = instance.getActiveAccount()

  if (isPrimaryAccount(activeAccount)) {
    return activeAccount
  }

  const accounts = instance.getAllAccounts()

  return accounts.find((account) => isPrimaryAccount(account)) || null
}

export const clearAppUserStorage = () => {
  /*
   * These values are user-specific and must not survive between different B2C users.
   */
  localStorage.removeItem('selectedImporterId')
  localStorage.removeItem('selectedImporterIdOwner')
  localStorage.removeItem('ivetted.lastProcessedLoginKey')

  /*
   * Old key from the previous attempted solution. Safe to remove if present.
   */
  sessionStorage.removeItem('ivetted.pendingLoginProcessAccountKey')
}

const AUTH_CACHE_VERSION_KEY = 'ivetted.auth.cache.version'

const currentAuthCacheVersion =
  process.env.REACT_APP_AUTH_CACHE_VERSION ||
  [
    'b2c-msal-v2',
    process.env.REACT_APP_B2C_CLIENT_ID,
    process.env.REACT_APP_B2C_AUTH_DOMAIN,
    process.env.REACT_APP_B2C_LOGIN_AUTHORITY,
    process.env.REACT_APP_B2C_SIGNUP_AUTHORITY,
    process.env.REACT_APP_B2C_SCOPE_1,
  ]
    .filter(Boolean)
    .join('|')

export const clearMsalBrowserStorage = ({ keepAuthCacheVersion = true } = {}) => {
  const clearStorage = (storage) => {
    if (!storage) {
      return
    }

    Object.keys(storage).forEach((key) => {
      const lowerKey = key.toLowerCase()

      if (
        lowerKey.startsWith('msal.') ||
        lowerKey.includes('msal') ||
        lowerKey === 'msal.interaction.status'
      ) {
        storage.removeItem(key)
      }
    })
  }

  clearStorage(localStorage)
  clearStorage(sessionStorage)
  clearAppUserStorage()

  if (!keepAuthCacheVersion) {
    localStorage.removeItem(AUTH_CACHE_VERSION_KEY)
  }
}

export const clearOldMsalCacheIfNeeded = () => {
  const existingVersion = localStorage.getItem(AUTH_CACHE_VERSION_KEY)

  if (existingVersion === currentAuthCacheVersion) {
    return
  }

  clearMsalBrowserStorage({ keepAuthCacheVersion: true })
  localStorage.setItem(AUTH_CACHE_VERSION_KEY, currentAuthCacheVersion)
}

export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_B2C_CLIENT_ID,

    /*
     * This must be the normal login/sign-up-sign-in authority used by the app.
     */
    authority: primaryAuthority,

    /*
     * Kept for your existing code if anything still reads msalConfig.auth.authorityRegister.
     * MSAL itself does not use this custom property.
     */
    authorityRegister: registerAuthority,

    /*
     * This should be only the host, for example:
     * ivettedb2c.b2clogin.com
     */
    knownAuthorities: [b2cPolicies.authorityDomain],

    redirectUri: interactiveRedirectUri,
    postLogoutRedirectUri: `${appOrigin}/`,
    navigateToLoginRequestUrl: false,

    extraQueryParameters: {
      tenant: process.env.REACT_APP_B2C_TENANT_NAME,
    },
  },

  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: isIE || isEdge || isFirefox,
  },

  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }

        // Enable temporarily while diagnosing authentication issues:
        // console.debug(message)
      },
      piiLoggingEnabled: false,
    },
  },
}

export const registerRequest = (tenantName) => ({
  authority: registerAuthority,
  scopes: [process.env.REACT_APP_B2C_SCOPE_1, 'openid', 'profile'],
  extraQueryParameters: {
    tenant: tenantName,
  },
})

export const loginRequest = {
  authority: primaryAuthority,
  scopes: [process.env.REACT_APP_B2C_SCOPE_1, 'openid', 'profile'],
  extraQueryParameters: {
    tenant: process.env.REACT_APP_B2C_TENANT_NAME,
  },
}

export const silentTokenRequest = {
  authority: primaryAuthority,
  scopes: [process.env.REACT_APP_B2C_SCOPE_1, 'openid', 'profile'],
  redirectUri: silentRedirectUri,
  extraQueryParameters: {
    tenant: process.env.REACT_APP_B2C_TENANT_NAME,
  },
}
